/**
 * Health and the Health Bar.
 *
 * RULE DCC-HP-001 - Rules for Survival p.41; Crawlers & Customization p.15.
 * A crawler's Health Bar has 10 slots. The value of each slot is the Con Mod.
 *
 * RULE DCC-HP-002 - Crawlers & Customization p.14.
 * Total Health is "Con x 10". Following the source's own convention (Rules
 * p.11: the abbreviation refers to the modifier, the full name to the score),
 * "Con" here is the MODIFIER. Two other passages confirm this reading:
 * C&C p.15 says to write "your Con (the modifier, not the score) in each Health
 * Bar slot", and Rules p.41 says the Health Bar "is determined by the Con Mod
 * for each slot". 10 slots x Con Mod therefore equals total Health.
 */

/** Crawlers have 10 slots. Mobs may have fewer, Bosses and Elites more (Rules p.41). */
export const CRAWLER_HEALTH_BAR_SLOTS = 10;

export interface HealthBar {
  /** Value of one slot, in Health. */
  slotValue: number;
  /** Total number of slots this creature has. */
  maxSlots: number;
  /** Slots not yet marked off. */
  slotsRemaining: number;
}

export function healthBarSlotValue(conMod: number): number {
  return conMod;
}

export function maxHealth(conMod: number, maxSlots: number = CRAWLER_HEALTH_BAR_SLOTS): number {
  return healthBarSlotValue(conMod) * maxSlots;
}

export function createFullHealthBar(
  conMod: number,
  maxSlots: number = CRAWLER_HEALTH_BAR_SLOTS,
): HealthBar {
  return { slotValue: healthBarSlotValue(conMod), maxSlots, slotsRemaining: maxSlots };
}

export function healthPercentRemaining(bar: HealthBar): number {
  if (bar.maxSlots === 0) return 0;
  return Math.round((bar.slotsRemaining / bar.maxSlots) * 100);
}

export interface HealthBarDamageResult {
  bar: HealthBar;
  slotsLost: number;
  /** Damage that did not add up to a whole slot and was lost. */
  damageIgnored: number;
  /** True when this damage took the creature to 0%. */
  reducedToZero: boolean;
}

/**
 * RULE DCC-HP-003 - Rules for Survival p.42.
 * Apply the post-mitigation damage to the Health Bar. Add up slot values until
 * the total meets or exceeds the damage; each whole slot value marks off one
 * slot. Damage that does not meet the value of a slot is lost. Slots already
 * marked off are not counted again.
 *
 * Damage must already have passed through mitigateDamage (rules/damage.ts).
 */
export function applyDamageToHealthBar(bar: HealthBar, damage: number): HealthBarDamageResult {
  if (!Number.isInteger(damage) || damage < 0) {
    throw new Error(`Damage must be a whole number of at least 0, received ${damage}`);
  }
  if (bar.slotValue <= 0) {
    throw new Error('A Health Bar slot must be worth at least 1 Health');
  }

  const wholeSlots = Math.floor(damage / bar.slotValue);
  const slotsLost = Math.min(wholeSlots, bar.slotsRemaining);
  const slotsRemaining = bar.slotsRemaining - slotsLost;

  return {
    bar: { ...bar, slotsRemaining },
    slotsLost,
    damageIgnored: damage - wholeSlots * bar.slotValue,
    reducedToZero: slotsRemaining === 0 && bar.slotsRemaining > 0,
  };
}

/**
 * RULE DCC-HP-004 - Rules for Survival p.43.
 * Healing is always done by whole slots, and a crawler cannot be healed above
 * their Health Bar maximum.
 */
export function healSlots(bar: HealthBar, slots: number): HealthBar {
  if (!Number.isInteger(slots) || slots < 0) {
    throw new Error(`Healing must be a whole number of slots, received ${slots}`);
  }
  return { ...bar, slotsRemaining: Math.min(bar.maxSlots, bar.slotsRemaining + slots) };
}

/**
 * RULE DCC-HP-005 - Rules for Survival pp.42-43.
 * Losing the last slot gives the Dying Debuff. The crawler must be healed to at
 * least 10% to survive, cannot act, Move, speak or use their HUD, and stays
 * Dying for a number of rounds equal to their Con (the modifier, by the
 * source's own convention - see docs/RULES_CONFLICTS.md OPEN QUESTION-002).
 */
export function isDying(bar: HealthBar): boolean {
  return bar.slotsRemaining === 0;
}

export function dyingRoundsFor(conMod: number): number {
  return conMod;
}

/** Mobs die instantly at 0%; crawlers and GM-controlled crawlers do not (Rules p.43). */
export function diesInstantlyAtZero(kind: 'character' | 'mob' | 'elite' | 'boss' | 'npc'): boolean {
  return kind === 'mob';
}
